import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  loginWithEmail,
  loginWithGoogle,
  loginWithFB,
  logout,
  signupUser,
  getProfile,
} from '../thunks/authThunks';

// Define a User type
interface User {
  id: string;
  name: string;
  email: string;
  token?: string;
  heartfeltToken: number;
}

// Define the state type
interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setLogout: (state) => {
      state.user = null;
      state.token = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    const handleRejected = (state: AuthState, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = typeof action.payload === 'string' ? action.payload : 'Something went wrong';
    };

    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token || null;
      })
      .addCase(loginWithEmail.rejected, handleRejected)

      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token || null;
      })
      .addCase(loginWithGoogle.rejected, handleRejected)

      .addCase(loginWithFB.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithFB.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token || null;
      })
      .addCase(loginWithFB.rejected, handleRejected)

      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
      })

      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token || null;
      })
      .addCase(signupUser.rejected, handleRejected)

      .addCase(getProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token || null;
      })
      .addCase(getProfile.rejected, handleRejected);
  },
});

export const { setUser, setLogout, setLoading } = authSlice.actions;
export default authSlice.reducer;
